import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useCreateTaxonomy, type TaxonomyScope } from '@/entities/taxonomy';
import { useCreateTaxonomyDataset } from '@/entities/taxonomy-dataset';
import { useUploadImportFile, type ImportState } from '@/entities/taxonomy-import';
import { ROUTES } from '@/shared/config/routes';

// eslint-disable-next-line i18next/no-literal-string -- persisted audit reason, not copy
const CREATE_REASON = 'Started from a file';

/** What the file is called without its extension, as a starting name the person can change. */
const baseName = (fileName: string) => fileName.replace(/\.[^.]+$/, '').slice(0, 255) || fileName;

/**
 * Starts from a naming file the way a person starts by hand: the file goes to the asset library,
 * a real draft schema or taxonomy is created, and its page opens with the file for its agent.
 */
export function useStartFromFile(options?: { onNameConflict?: (message: string) => void }) {
  const { t } = useTranslation('knowledge');
  const navigate = useNavigate();
  const upload = useUploadImportFile();
  const createSchema = useCreateTaxonomy();
  const createTaxonomy = useCreateTaxonomyDataset(
    options?.onNameConflict ? { onNameConflict: options.onNameConflict } : undefined,
  );
  const [error, setError] = useState<string | null>(null);

  async function uploaded(file: File) {
    setError(null);
    try {
      return await upload.mutateAsync(file);
    } catch {
      setError(t('taxonomy.import.startFailed'));
      return null;
    }
  }

  /** A new draft schema, built from the file on its page; a taxonomy of its rows may follow. */
  async function schemaFromFile(
    file: File,
    scope: TaxonomyScope,
    thenTaxonomy?: ImportState['thenTaxonomy'],
  ) {
    const importFile = await uploaded(file);
    if (!importFile) return;
    const schema = await createSchema.mutateAsync({
      name: baseName(file.name),
      scope,
      reason: CREATE_REASON,
    });
    const state: ImportState = { importFile, ...(thenTaxonomy ? { thenTaxonomy } : {}) };
    void navigate(ROUTES.KNOWLEDGE_HUB_TAXONOMY_DETAIL(schema.id), { state });
  }

  /** A new taxonomy on a published schema, filled with the file's rows on its page. */
  async function taxonomyFromFile(
    file: File,
    schema: { taxonomyId: string; versionId: string },
    campaignId: string | null,
  ) {
    const importFile = await uploaded(file);
    if (!importFile) return;
    const created = await createTaxonomy.mutateAsync({
      name: baseName(file.name),
      taxonomyId: schema.taxonomyId,
      taxonomyVersionId: schema.versionId,
      campaignId,
    });
    const state: ImportState = { importFile };
    void navigate(ROUTES.TOOLS_TAXONOMY_GENERATION_DETAIL(created.id), { state });
  }

  return {
    schemaFromFile: (...args: Parameters<typeof schemaFromFile>) =>
      schemaFromFile(...args).catch(() => undefined),
    taxonomyFromFile: (...args: Parameters<typeof taxonomyFromFile>) =>
      taxonomyFromFile(...args).catch(() => undefined),
    pending: upload.isPending || createSchema.isPending || createTaxonomy.isPending,
    error,
    clearError: () => setError(null),
  };
}
